import React, { useState, useEffect, useRef, useCallback } from "react";
import ct from "countries-and-timezones";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  CardHeader,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import "../../../Components/Common/crop.css";
import { getDLDealCroppedImg } from "../../../Components/Common/DealCropDL";

const ALL_TIMEZONES = Object.keys(ct.getAllTimezones()).sort();

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function AddHappyHour() {
  const history = useHistory();
  const { businessId } = useParams();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    categoryId: "",
    days: [],
    description: "",
    startTime: "",
    endTime: "",
    timezone: "",
    removePhoto: false,
  });

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [dealPhoto, setdealPhoto] = useState([]);
  const [dealPhotoPreviews, setDealPhotoPreviews] = useState([]);

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}categories`
        );
        const data = await res.json();
        setCategories(data.responseData?.categories || []);
      } catch {
        toast.error("Failed to load categories");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  // Cropper
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const loadNextImage = (files) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setPendingFiles(files);
    loadNextImage(files);
    e.target.value = "";
  };

  const handleCropSave = async () => {
    try {
      const blob = await getDLDealCroppedImg(
        imageToCrop,
        croppedAreaPixels
      );
      const file = new File([blob], `deal-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      setdealPhoto((prev) => [...prev, file]);
      setDealPhotoPreviews((prev) => [
        ...prev,
        URL.createObjectURL(blob),
      ]);

      const remaining = pendingFiles.slice(1);
      setPendingFiles(remaining);

      if (remaining.length > 0) {
        loadNextImage(remaining);
      } else {
        setShowCropper(false);
      }
    } catch {
      toast.error("Image crop failed");
    }
  };

  const handleRemovePhoto = (index) => {
    setdealPhoto((prev) => prev.filter((_, i) => i !== index));
    setDealPhotoPreviews((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!businessId) throw new Error("Business ID missing in URL");
      if (!formData.categoryId)
        throw new Error("Category is required");
      if (formData.days.length === 0)
        throw new Error("Select at least one day");
      if (!formData.description.trim())
        throw new Error("Description is required");
      if (!formData.startTime || !formData.endTime)
        throw new Error("Start and End time are required");
      if (!formData.timezone)
        throw new Error("Timezone is required");

      const today = new Date().toISOString().split("T")[0];
      const toISO = (t) => t ? `${today}T${t}:00` : "";

      const payload = new FormData();
      payload.append("categoryId", formData.categoryId);
      payload.append("description", formData.description);
      payload.append("startTime", toISO(formData.startTime));
      payload.append("endTime", toISO(formData.endTime));

      dealPhoto.forEach((photo) => {
        payload.append("dealPhoto", photo);
      });

      payload.append("timezone", formData.timezone);
      payload.append("removePhoto", String(formData.removePhoto));
      formData.days.forEach((d) => payload.append("days[]", d));

      console.log("Add HappyHour Payload:", Object.fromEntries(payload));

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/deal/happyHours/${businessId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "accessToken"
            )}`,
          },
          body: payload,
        }
      );

      const data = await res.json();
      if (!res.ok)
        throw new Error(
          data.responseMessage?.join(", ") || "Server Error"
        );

      toast.success("Happy Hour added successfully!");
      setTimeout(
        () => history.push(`/business-deals/${businessId}`),
        1200
      );
    } catch (err) {
      toast.error(err.message || "Failed to add Happy Hour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <MetaTags>
          <title>Add Happy Hour</title>
        </MetaTags>
        <ToastContainer />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className="d-flex align-items-center">
                <Link to={`/business-deals/${businessId}`}>
                  <Button color="primary">
                    <i className="mdi mdi-arrow-left" />
                  </Button>
                </Link>
                <h4 className="mb-0 ms-2">Add Happy Hour</h4>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          name="categoryId"
                          value={formData.categoryId}
                          onChange={handleInputChange}
                          disabled={categoriesLoading}
                        >
                          <option value="">Select category</option>
                          {categories.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md={6} className="mt-3">
                      <FormGroup>
                        <Label>Deal Photos</Label>
                        <Button
                          type="button"
                          className="ms-2"
                          color="primary"
                          onClick={() =>
                            fileInputRef.current.click()
                          }
                        >
                          Choose Files
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileChange}
                          hidden
                        />

                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {dealPhotoPreviews.map((src, index) => (
                            <div
                              key={index}
                              className="position-relative"
                            >
                              <img
                                src={src}
                                alt="preview"
                                className="rounded border"
                                width="100"
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                onClick={() =>
                                  handleRemovePhoto(index)
                                }
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="Enter happy hour description"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleInputChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          name="endTime"
                          value={formData.endTime}
                          onChange={handleInputChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Timezone</Label>
                        <Input
                          type="select"
                          name="timezone"
                          value={formData.timezone}
                          onChange={handleInputChange}
                        >
                          <option value="">Select timezone</option>
                          {ALL_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Label>Days</Label>
                      <div className="d-flex flex-wrap gap-2">
                        {DAYS.map((day) => (
                          <FormGroup check key={day}>
                            <Input
                              type="checkbox"
                              checked={formData.days.includes(day)}
                              onChange={() =>
                                handleDayToggle(day)
                              }
                            />
                            <Label check>{day}</Label>
                          </FormGroup>
                        ))}
                      </div>
                    </Col>
                  </Row>

                  <Button
                    type="submit"
                    color="primary"
                    className="mt-4"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Crop Image</h4>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowCropper(false)}
                >
                  &times;
                </button>
              </div>

              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={4 / 3}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>

              <div className="crop-controls">
                <label>Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) =>
                    setZoom(Number(e.target.value))
                  }
                />
              </div>

              <div className="crop-buttons">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCropper(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCropSave}
                >
                  Save Crop
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default AddHappyHour;
